import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";

import { AuthProvider, useAuth } from "@/lib/auth";
import ProtectedRoute from "@/components/ProtectedRoute";
import AdminLayout from "@/components/AdminLayout";
import { Toaster } from "@/components/ui/sonner";

import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Leads from "@/pages/Leads";
import LeadDetail from "@/pages/LeadDetail";
import Users from "@/pages/Users";
import Settings from "@/pages/Settings";
import Estimates from "@/pages/Estimates";
import EstimateForm from "@/pages/EstimateForm";
import EstimateDetail from "@/pages/EstimateDetail";
import Jobs from "@/pages/Jobs";
import JobDetail from "@/pages/JobDetail";
import Schedule from "@/pages/Schedule";
import Invoices from "@/pages/Invoices";
import InvoiceDetail from "@/pages/InvoiceDetail";
import PublicEstimate from "@/pages/PublicEstimate";
import OnboardingInvite from "@/pages/OnboardingInvite";
import OnboardingWizard from "@/pages/OnboardingWizard";
import AdminOnboarding from "@/pages/AdminOnboarding";
import AdminHandbook from "@/pages/AdminHandbook";
import TrainingHome from "@/pages/TrainingHome";
import TrainingWatch from "@/pages/TrainingWatch";
import AdminTraining from "@/pages/AdminTraining";
import BidIntake from "@/pages/BidIntake";
import BidIntakeDetail from "@/pages/BidIntakeDetail";
import AgentChat from "@/pages/AgentChat";
import AgentReview from "@/pages/AgentReview";
import Usage from "@/pages/Usage";

// Todd asked (2026-09-23): agent chat should be the FIRST thing after login.
// "/" now lands on Jeff (agent chat) for admins/supervisors; crew keep Dashboard.
function AgentHome() {
    const { user } = useAuth();
    const role = user?.role;
    if (role === "admin" || role === "supervisor") return <AgentChat />;
    return <Dashboard />;
}

// Force crew/contractor users into onboarding until they're complete.
function OnboardingGate({ children }) {
    const { user } = useAuth();
    if (
        user &&
        user !== false &&
        ["crew", "contractor"].includes(user.role) &&
        user.onboarding_status !== "complete"
    ) {
        return <Navigate to="/onboarding" replace />;
    }
    return children;
}

export default function App() {
    return (
        <AuthProvider>
            <Toaster position="bottom-center" richColors closeButton />
            <BrowserRouter>
                <Routes>
                    {/* Public client-facing estimate view */}
                    <Route path="/e/:token" element={<PublicEstimate />} />

                    {/* Public onboarding invitation */}
                    <Route path="/onboarding/invite/:token" element={<OnboardingInvite />} />

                    {/* Public auth routes */}
                    <Route path="/login" element={<Login />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />

                    {/* Authenticated onboarding wizard (no admin shell) */}
                    <Route
                        path="/onboarding"
                        element={
                            <ProtectedRoute>
                                <OnboardingWizard />
                            </ProtectedRoute>
                        }
                    />

                    {/* Protected admin app shell */}
                    <Route
                        element={
                            <ProtectedRoute>
                                <OnboardingGate>
                                    <AdminLayout />
                                </OnboardingGate>
                            </ProtectedRoute>
                        }
                    >
                        <Route path="/" element={<AgentHome />} />
                        <Route path="/leads" element={<Leads />} />
                        <Route path="/leads/:id" element={<LeadDetail />} />
                        <Route path="/estimates" element={<Estimates />} />
                        <Route path="/estimates/new" element={<EstimateForm />} />
                        <Route path="/estimates/:id" element={<EstimateDetail />} />
                        <Route path="/jobs" element={<Jobs />} />
                        <Route path="/jobs/:id" element={<JobDetail />} />
                        <Route path="/schedule" element={<Schedule />} />
                        <Route path="/invoices" element={<Invoices />} />
                        <Route path="/invoices/:id" element={<InvoiceDetail />} />
                        <Route
                            path="/team"
                            element={
                                <ProtectedRoute roles={["admin", "supervisor"]}>
                                    <Users />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/onboarding"
                            element={
                                <ProtectedRoute roles={["admin", "supervisor"]}>
                                    <AdminOnboarding />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/onboarding/:userId"
                            element={
                                <ProtectedRoute roles={["admin", "supervisor"]}>
                                    <AdminOnboarding />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/handbook"
                            element={
                                <ProtectedRoute roles={["admin", "supervisor"]}>
                                    <AdminHandbook />
                                </ProtectedRoute>
                            }
                        />
                        <Route path="/settings" element={<Settings />} />
                        <Route
                            path="/usage"
                            element={
                                <ProtectedRoute roles={["admin", "supervisor"]}>
                                    <Usage />
                                </ProtectedRoute>
                            }
                        />
                        <Route path="/training" element={<TrainingHome />} />
                        <Route path="/training/:id" element={<TrainingWatch />} />
                        <Route
                            path="/admin/training"
                            element={
                                <ProtectedRoute roles={["admin", "supervisor"]}>
                                    <AdminTraining />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/bid-intake"
                            element={
                                <ProtectedRoute roles={["admin", "supervisor"]}>
                                    <BidIntake />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/admin/bid-intake/:id"
                            element={
                                <ProtectedRoute roles={["admin", "supervisor"]}>
                                    <BidIntakeDetail />
                                </ProtectedRoute>
                            }
                        />
                        <Route path="/agent" element={<AgentChat />} />
                        <Route
                            path="/agent/review"
                            element={
                                <ProtectedRoute roles={["admin", "supervisor"]}>
                                    <AgentReview />
                                </ProtectedRoute>
                            }
                        />
                        <Route path="/" element={<AgentHome />} />
                        <Route path="*" element={<Navigate to="/agent" replace />} />
                    </Route>
                </Routes>
            </BrowserRouter>
        </AuthProvider>
    );
}
